import { createContext, useContext, useEffect, useState } from "react";
import { Redirect } from "react-router-dom";
import { showErrorToast } from "../components/Toast";
import { menuAPI } from "../api/api";

const FALLBACK_HOME_PATH = "/app/dashboard";

const AuthContext = createContext();

const isNavigablePath = (path) =>
  typeof path === "string" && path.trim() !== "" && path.trim() !== "#";

const buildMenuTree = (menuList = [], parentId = null) => {
  return menuList
    .filter((menu) => menu.parentId === parentId)
    .sort((a, b) => (a.orderIdx ?? 0) - (b.orderIdx ?? 0))
    .map((menu) => ({
      path: menu.path,
      icon: menu.icon,
      name: menu.name,
      code: menu.code,
      routes: buildMenuTree(menuList, menu.id),
    }));
};

/** Path of the first menu in sidebar order; parent groups without path use the first child. */
export const getFirstMenuPath = (menuList = []) => {
  const findPath = (nodes) => {
    for (const node of nodes) {
      if (isNavigablePath(node.path)) {
        return node.path.trim();
      }
      if (node.routes?.length) {
        const childPath = findPath(node.routes);
        if (childPath) return childPath;
      }
    }
    return null;
  };
  return findPath(buildMenuTree(menuList));
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [shipperToken, setShipperToken] = useState(
    localStorage.getItem("shipperToken"),
  );

  const [filteredMenu, setFilteredMenu] = useState([]);
  const [menu, setMenu] = useState([]);
  const [defaultPath, setDefaultPath] = useState(null);

  const fetchMenu = async () => {
    try {
      const res = await menuAPI.getMenuByUser();
      const data = res.data || [];
      const result = buildMenuTree(data);
      const firstPath = getFirstMenuPath(data) || FALLBACK_HOME_PATH;

      setFilteredMenu(result);
      setMenu(data);
      setDefaultPath(firstPath);
      return firstPath;
    } catch (error) {
      showErrorToast(error.message);
      setDefaultPath(FALLBACK_HOME_PATH);
      return FALLBACK_HOME_PATH;
    }
  };

  const shipperLogin = (token) => {
    localStorage.setItem("shipperToken", token);
    setShipperToken(token);
  };

  const shipperLogout = () => {
    localStorage.removeItem("shipperToken");
    setShipperToken(null);
  };

  const getShipperToken = () => {
    return JSON.parse(shipperToken) || { code: "", accessToken: "" };
  };

  const login = (token) => {
    localStorage.setItem("token", token);
    setToken(token);
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setMenu([]);
    setFilteredMenu([]);
    setDefaultPath(null);
  };

  const getTokenInfo = () => {
    return JSON.parse(token);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        login,
        logout,
        getTokenInfo,
        menu,
        filteredMenu,
        defaultPath,
        fetchMenu,
        shipperToken,
        shipperLogin,
        shipperLogout,
        getShipperToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);

// Middleware
export const ProtectedRoute = ({ children }) => {
  const { token, fetchMenu } = useAuth();
  let exp = 0;

  useEffect(() => {
    if (token) {
      fetchMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (token) {
    const { expiration } = JSON.parse(token);
    exp = expiration;
  }

  return token && exp > Date.now() ? (
    children
  ) : (
    <Redirect from="/" to="/login" />
  );
};

export const LoginSuccessRoute = ({ children }) => {
  const { token, defaultPath, fetchMenu } = useAuth();
  const [resolvedPath, setResolvedPath] = useState(null);

  let exp = 0;

  if (token) {
    const { expiration } = JSON.parse(token);
    exp = expiration;
  }

  const isLoggedIn = Boolean(token && exp > Date.now());

  useEffect(() => {
    if (!isLoggedIn) {
      setResolvedPath(null);
      return undefined;
    }
    if (defaultPath) {
      setResolvedPath(defaultPath);
      return undefined;
    }
    let cancelled = false;
    fetchMenu().then((path) => {
      if (!cancelled) setResolvedPath(path || FALLBACK_HOME_PATH);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, defaultPath]);

  if (!isLoggedIn) return children;
  if (!resolvedPath) return null;
  return <Redirect to={resolvedPath} />;
};

export const ProtectedRouteShipping = ({ children }) => {
  const { shipperToken } = useAuth();
  let exp = 0;

  if (shipperToken) {
    const { expiration } = JSON.parse(shipperToken);
    exp = expiration;
  }

  return shipperToken && exp > Date.now() ? (
    children
  ) : (
    <Redirect to="/shipping/login" />
  );
};

export const ShippingLoginSuccessRoute = ({ children }) => {
  const { shipperToken } = useAuth();
  let exp = 0;

  if (shipperToken) {
    const { expiration } = JSON.parse(shipperToken);
    exp = expiration;
  }

  return shipperToken && exp > Date.now() ? (
    <Redirect from="/" to="/shipping/tracking" />
  ) : (
    children
  );
};
